import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  IApiError,
  IDropdownItemStations,
  isTranslatableItem,
  Log,
  normalizeErrors,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createMessage,
  INvidiaShuttleChooseStationLandingModel,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  LandingStepEnum,
  NvidiaShuttleChooseStationStepFormFieldsEnum,
  useLandingActions,
  useLandingApiStations,
  useLandingsAuth,
} from "@ryde/utils-landings";

import { useYupChooseStationLandingValidationSchema } from "./parts";

export const useStepChooseStationNvidiaShuttle = () => {
  const { goToStep } = useLandingActions();
  const { getOfficialStationsByHomeAddress, setPassengerPickupStation } =
    useLandingApiStations();
  const setStationsDropdownList = useSetRecoilState(
    landingDropdownStationListAtom,
  );
  const { yupChooseStationLandingValidationSchema } =
    useYupChooseStationLandingValidationSchema();
  const { t } = useTranslation();

  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { handleLogout } = useLandingsAuth();

  const resolver = useYupValidationResolver(
    yupChooseStationLandingValidationSchema,
    "landing",
  );

  const methods = useForm<INvidiaShuttleChooseStationLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      employeeNumber: "",
      firstName: "",
      lastName: "",
      phoneMobile: "",
      homeAddress: {
        fullAddress: "",
        longitude: undefined,
        latitude: undefined,
      },
      sourceStation2Id: undefined,
      destinationId: undefined,
      acceptTerms: false,
      acceptPrivacyPolicy: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    setError,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const homeAddressWatchValue = watch(
    NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress,
  );
  const sourceStation2IdWatchValue = watch(
    NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
  );
  const destinationIdWatchValue = watch(
    NvidiaShuttleChooseStationStepFormFieldsEnum.DestinationId,
  );

  /**
   * The consent checkboxes are the last thing the page asks for, and they are the only fields the schema
   * requires that no template writes: without `shouldValidate` the value lands but `isValid` keeps the
   * stale `false` it held while the boxes were unticked, leaving the continue button dead until some
   * other field revalidates the form.
   */
  const handleConsentToggle = useEvent(
    (
      field:
        | NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptTerms
        | NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
    ): void => {
      setValue(field, !getValues(field), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const handleHomeAddressChange = useEvent(async (): Promise<void> => {
    setServerErrors(undefined);
    const homeAddress = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress,
    );

    if (!homeAddress.latitude || !homeAddress.longitude) {
      setError(NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress, {
        message: t("errors.missingCoordinates"),
      });
      setValue(
        NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
        undefined,
      );
      setStationsDropdownList([]);
      return;
    }

    try {
      const response = await getOfficialStationsByHomeAddress({
        homeAddress,
      });

      const { success, data } = response ?? {};

      if (success && data) {
        if (
          "isHomeAddressInPermittedPolygon" in data &&
          !data.isHomeAddressInPermittedPolygon
        ) {
          setError(NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress, {
            message: t("errors.notInPermittedPolygon"),
          });
        }

        if (data.officialStationsList?.length) {
          setStationsDropdownList(
            data.officialStationsList as IDropdownItemStations[],
          );
        }

        setValue(
          NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
          undefined,
        );
        clearErrors(
          NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
        );

        if (data.selectedStationId) {
          setValue(
            NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
            data.selectedStationId,
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
        } else {
          setError(
            NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
            {
              message: t("errors.noNearbyStationFoundMessage"),
            },
          );
        }
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setServerErrors(createMessage(errorsNormalized));
      Log.warnAndToast(errorsNormalized, error);
    }
  });

  const handleChooseStation = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const homeAddress = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress,
    );
    const sourceStation2Id = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
    );
    const employeeNumber = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.EmployeeNumber,
    );
    const firstName = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.FirstName,
    );
    const lastName = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.LastName,
    );
    const phoneMobile = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.PhoneMobile,
    );
    const destinationId = getValues(
      NvidiaShuttleChooseStationStepFormFieldsEnum.DestinationId,
    );

    if (sourceStation2Id && homeAddress) {
      setApiLoading(true);

      try {
        const acceptTerms = getValues(
          NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptTerms,
        );
        const acceptPrivacyPolicy = getValues(
          NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
        );
        const response = await setPassengerPickupStation({
          homeAddress,
          sourceStation2Id,
          workerId: employeeNumber,
          destinationStationId: destinationId,
          firstName,
          lastName,
          phone: phoneMobile,
          acceptTerms,
          acceptPrivacyPolicy,
        });

        const { success, data, error } = response ?? {};

        if (success && data) {
          setServerErrors(createMessage([]));
          goToStep(LandingStepEnum.FinishedStep);
          handleLogout();
          return;
        }

        if (!success && error) {
          const errorMessage = isTranslatableItem(error)
            ? error.translationKey
            : "errors.updateNotSuccess";
          setServerErrors(createMessage([errorMessage]));
          setApiLoading(false);
          return;
        }
      } catch (error) {
        Log.error(
          "failed update passenger homeStation and sourceStation2Id. error:",
          error,
        );
      } finally {
        setApiLoading(false);
      }

      setServerErrors(createMessage(["errors.updateNotSuccess"]));
    }
  });

  const isContinueCanBeApply =
    isValid &&
    homeAddressWatchValue.fullAddress &&
    homeAddressWatchValue.latitude &&
    homeAddressWatchValue.longitude &&
    sourceStation2IdWatchValue &&
    destinationIdWatchValue;

  return {
    methods,
    handleChooseStation,
    handleConsentToggle,
    handleHomeAddressChange,
    isContinueCanBeApply,
  };
};
